<template>
  <div :class="[containerClasses, { 'szw-gap-[12px]': !branded }]" ref="containerRef" v-bind="containerBindings">
    <fallback-screen-handler>
      <branding v-if="branded" :brand="activeTheme" />
      <!-- CAPTION HEADER -->
      <caption-header v-bind="captionHeaderProps">
        <div class="szw-flex szw-flex-col szw-h-full szw-w-full szw-max-w-[768px] szw-relative">
          <div class="szw-absolute szw-w-full szw-max-w-[768px] szw-h-10 szw-flex szw-justify-between">
            <div class="szw-z-50 szw-cursor-pointer szw-w-[40px] szw-h-[40px] szw-justify-center szw-items-center szw-flex" @click="scrollLeft">
              <sprite sprite-id="arrow-back" class="szw-text-latest-color-primary"/>
            </div>
            <div class="szw-z-50 szw-180-rotate szw-cursor-pointer szw-w-[40px] szw-h-[40px] szw-justify-center szw-items-center szw-flex" @click="scrollRight">
              <sprite sprite-id="arrow-back" class="szw-text-latest-color-primary"/>
            </div>
          </div>
          <stages-stripe :stages="knockoutStore.stageDurationStripe" :translateX="knockoutBloc.translateX.value" :card-width="knockoutBloc.cardWidth.value"/>
        </div>
      </caption-header>

      <div class="szw-flex szw-flex-col szw-h-full szw-w-full szw-max-w-[768px] szw-mx-auto szw-gap-1">
        <!-- KNOCKOUT -->
        <scrolling-container
          :widget-container-height="!branded ? widgetHeight : widgetHeight + 2"
          :container-ref="knockoutContainerRef"
          :container-id="`${activeTab.id}`"
          :override-scroll-offset="CARD_HEIGHT * 2 + 16"
          :disable-bottom-navigation="!knockoutBloc.hasBottomNavigation.value"
          @recalculated="knockoutBloc.shouldDisableScrollingContainerTransition.value = false"
          :override-bottom-navigation-visibility="true"
          :full-width-buttons="false"
          @scroll-index-changed="handleScrollIndexChanged"
          :is-down-navigation-disabled="knockoutBloc.isDownNavigationDisabled.value"
        >
          <div class="szw-scrollable-container szw-gap-0" :style="knockoutBloc.scrollableContainerStyle.value" ref="knockoutContainerRef">
            <div class="szw-flex szw-flex-row szw-gap-6 szw-relative" :style="{ transform: knockoutBloc.translateX.value, transition: 'transform .3s' }">
              <div class="szw-flex szw-flex-col szw-gap-[4px] szw-flex-1 szw-relative" v-for="(lane, laneIndex) in knockoutStore.stages" :key="laneIndex">
                <template v-if="status === 'success'">
                  <lane-item
                    v-for="(laneItem, laneItemIndex) of lane"
                    :sport-type="sportType"
                    :lane-item="laneItem"
                    :lane-item-index="laneItemIndex"
                    :lane-index="laneIndex"
                    :lane-item-style="{
                      minWidth: '208px',
                      maxWidth: getDrawItemMaximumWidth(laneItem),
                      width: `${knockoutBloc.cardWidth.value}px`,
                      margin: getDrawItemMargin(laneIndex, activeTab.index, laneItem),
                      cursor: !isKnockoutDetailsVisible || hasVirtualTeam(laneItem.events?.[0]) ? 'default' : 'pointer'
                    }"
                    :has-bracket-lines="activeTab.index !== knockoutBloc.lanesNumber.value - 1 && laneItem.stage !== '3rd_place_final'"
                    :has-third-place-final="hasThirdPlaceFinal && laneItem.stage === '3rd_place_final'"
                    :lane-item-line-style="knockoutBloc.getDrawLineStyle(laneIndex, activeTab.index, knockoutBloc.spacing.value)"
                    :helpers="{
                      getTime: knockoutBloc.getTime,
                      formatDate: knockoutBloc.formatDate
                    }"
                    @lane-item-click="toggleKnockoutDetails"
                    :key="laneItem.roundId"
                    :is-small-widget-container-size="isSmallWidget"
                  />
                </template>

                <knockout-skeleton
                  v-if="status === 'loading'"
                  :lane="lane"
                  :width="`${knockoutBloc.cardWidth.value}px`"
                  :margin="`${knockoutBloc.calculateDrawCardMarginStep(laneIndex, activeTab.index) * knockoutBloc.spacing.value}px 0`"
                  :drawStyle="knockoutBloc.getDrawLineStyle(laneIndex, activeTab.index, knockoutBloc.spacing.value)"
                  :lanesNumber="knockoutBloc.lanesNumber.value"
                />
              </div>
            </div>
          </div>
        </scrolling-container>
        
      <events-popup v-if="showKnockoutDetails" :liveScoreboardProps="liveScoreboardProps" :event="chosenNode.activeEvent" @popup-close="handleCloseEventPopup" :sportType="sportType"/>
      </div>
    </fallback-screen-handler>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, provide, ref, watch } from 'vue'
import FallbackScreenHandler from '@/__core__/components/fallbackScreens/FallbackScreenHandler.vue'
import useFetch from '@/__core__/composables/useFetch'
import CaptionHeader from '@/__v1.5__/components/caption-headers/CaptionHeader.vue'
import ScrollingContainer from '@/__core__/containers/scrolling-container/ScrollingContainer.vue'
import StagesStripe from './components/StagesStripe.vue'
import { useKnockoutStore } from './Knockout.store'
import { useDataRefresh } from '@/__core__/data-refresh/useDataRefresh'
import { useKnockoutBloc } from './useKnockoutBloc'
import KnockoutSkeleton from './components/KnockoutSkeleton.vue'
import LaneItem from '@/widgets/knockout/components/LaneItem.vue'
import '@/__core__/style/style.scss'
import { useWidgetContext } from '@/__v1.5__/composables/useWidgetContext'
import Sprite from '@/__core__/components/sprite/Sprite.vue'
import Branding from '@/__v1.5__/components/branding/Branding.vue'
import EventsPopup from '@/__v1.5__/components/popups/events-popup/EventsPopup.vue'

const widgetId = 'knockout'

const props = defineProps({
  seasonId: {
    type: String,
    default: 'sr:season:119781'
  },
  sportType: {
    type: String,
    default: 'football'
  },
  languageIdentifier: {
    type: String,
    default: 'eng'
  },
  size: {
    type: Number,
    default: 640
  },
  theme: {
    type: String,
    default: 'light'
  },
  region: {
    type: String,
    default: ''
  },
  themeConfig: {
    type: Object,
    default: () => null
  }
})

const CARD_HEIGHT = 88

/** @type {Ref<HTMLDivElement>} */
const containerRef = ref(null)
const knockoutContainerRef = ref(null)

const activeIndex = ref(0)
const activeTab = ref({
  id: 0,
  name: '',
  index: 0
})

const showKnockoutDetails = ref(false)
const chosenNode = ref(null)

const scrollIndex = ref(0)
const { activeTheme, branded, containerClasses, langId, widgetHeight, isSmallWidget, widgetContainerPaddinglessWidth, widgetWidth } = useWidgetContext({
  ...props,
  containerRef,
  widgetId
})

const knockoutStore = useKnockoutStore()
const knockoutBloc = useKnockoutBloc(knockoutStore, props.languageIdentifier, activeTab, isSmallWidget, widgetContainerPaddinglessWidth, CARD_HEIGHT, scrollIndex)
const { startRefresh } = useDataRefresh(60 * 1000, widgetId)

const { status } = useFetch(async () => {
  const payload = {
    seasonId: props.seasonId,
    sportType: props.sportType,
    languageIdentifier: props.languageIdentifier,
    region: props.region
  }

  await knockoutStore.fetchKnockout(payload)

  startRefresh(
    async () => knockoutStore.scheduleKnockoutDataRefresh(payload),
    () => knockoutStore.getShouldCallApi(),
    () => knockoutStore.getShouldStopRefresh
  )
})

const adjustedTabs = computed(() => knockoutStore.adjustedTabs(!isSmallWidget.value))
const hasThirdPlaceFinal = computed(() => knockoutStore.stages.some(lane => lane.some(node => node.stage === '3rd_place_final')))

const hasVirtualTeam = event => event?.teams && event?.teams?.some(team => team.virtual)

const handleTabClicked = (tab, index) => {
  knockoutBloc.shouldDisableScrollingContainerTransition.value = true
  activeTab.value = tab
  activeIndex.value = index
}

const scrollLeft = () => {
  if (activeIndex.value > 0) {
    const newIndex = activeIndex.value - 1
    handleTabClicked(adjustedTabs.value[newIndex], newIndex)
  }
}

const scrollRight = () => {
  if (activeIndex.value < adjustedTabs.value.length - 1) {
    const newIndex = activeIndex.value + 1
    handleTabClicked(adjustedTabs.value[newIndex], newIndex)
  }
}

const isKnockoutDetailsVisible = computed(() => !['tennis'].includes(props.sportType))

const toggleKnockoutDetails = ({ events, activeEvent }) => {
  if ((activeEvent && hasVirtualTeam(activeEvent)) || !isKnockoutDetailsVisible.value) {
    return
  }
  showKnockoutDetails.value = !showKnockoutDetails.value
  chosenNode.value = activeEvent ? { activeEvent, id: activeEvent.eventId, events } : null
}

const getDrawItemMaximumWidth = drawItem => {
  let maxWidth = ''

  if (drawItem.stage === '3rd_place_final' && !isSmallWidget.value) {
    maxWidth = '208px'
  }

  return maxWidth
}

const getDrawItemMargin = (laneIndex, activeTabIndex, drawItem) => {
  if (hasThirdPlaceFinal.value && ['final', '3rd_place_final'].includes(drawItem.stage)) {
    return drawItem.stage === 'final' ? '150px 0 0 0' : '28px auto 0 auto'
  }

  return `${knockoutBloc.calculateDrawCardMarginStep(laneIndex, activeTabIndex) * knockoutBloc.spacing.value}px 0`
}

const handleScrollIndexChanged = scrollIndexValue => {
  scrollIndex.value = scrollIndexValue
}

const unwatch = watch(
  () => isSmallWidget?.value,
  newIsSmall => {
    const isLastOrSecondLast = [knockoutBloc.lanesNumber.value - 1, knockoutBloc.lanesNumber.value - 2].includes(activeTab.value.index)

    if (newIsSmall && isLastOrSecondLast) {
      activeTab.value = adjustedTabs.value.find(tab => tab.index === knockoutBloc.lanesNumber.value - 1)
    } else if (isLastOrSecondLast) {
      activeTab.value = adjustedTabs.value.find(tab => tab.index === knockoutBloc.lanesNumber.value - 2)
    }
  }
)

const captionHeaderProps = computed(() => ({
  title: knockoutStore.name,
  subtitle: knockoutStore.duration,
  logoUrl: knockoutStore.competition?.logo?.light?.medium || '',
  disableTabs: true,
  hasShadow: false
}))

const containerBindings = computed(() => ({
  'e2e-id': 'szw-knockout-widget',
  'theme-id': activeTheme.value,
  'lang-id': langId.value,
  'season-id': props.seasonId,
  'drf-id': widgetId
}))

function handleCloseEventPopup() {
  showKnockoutDetails.value = false
}

const liveScoreboardProps = computed(() => {
  return chosenNode.value.events.map(event => ({
    languageIdentifier: langId,
    eventId: event.eventId,
    sportType: props.sportType === 'basketball' ? 'global_basketball' : props.sportType,
    theme: activeTheme.value,
    size: widgetWidth.value,
    themeConfig: props.themeConfig,
    timelineTypes: ['score_change']
  }))
})

onBeforeUnmount(() => {
  unwatch()
})

provide('status', status)
provide('isSmallWidgetContainerSize', true)
</script>
